// Run pace + run modifiers (owner 2026-11-02, decided via Q&A). IP-NEUTRAL. Pure: no DOM, no three, no state.
//
// PACE: 'travel slower, same damage per second'. One factor on how fast things TRAVEL — the hero's run speed, foe run
// speed, every shot / disc / wall / wave in flight (hero and foe), and foe attack wind-ups (they last 1 / PACE as long, so
// they are easier to read). It is NOT a time scale: the run clock, the spawn timeline, boss times, skill cooldowns and
// skill damage stay in real seconds, so the hero's damage per second is unchanged. Ranges and areas never shrink: a shot
// that slows down flies for longer so it still reaches as far. See Sim.pace for where it is applied (and what is left
// out on purpose: knockback, pickup pull, Singularity drift, orbits / sweeps / blinks / leaps).
//
// Run modifiers: optional handicaps picked on the Run Setup screen. Each adds a share of Fracture Shards at the end of
// the run; the shares add up (the seven challenge modifiers together = +145 %). Relaxed is the one gentle modifier: its share is
// NEGATIVE (-30 %), so it can't be farmed. Difficulty presets (Relaxed / Normal / Hard / Brutal) are bundles of these.. They reach the Sim through SimOptions.mods, so they are deterministic
// and unit-testable.
import type { PassiveId } from './content';

import { INNATE, WEAPONS, type WeaponId } from './content';
import { Rng } from './util';

/** default pace: 15 % slower travel than the original tuning */
export const PACE = 0.85;
/** pace with the Hyper modifier: 10 % faster than the original tuning */
export const PACE_HYPER = 1.1;

export type ModId = 'hyper' | 'swarm' | 'tough' | 'hurry' | 'noheal' | 'elites' | 'night' | 'relaxed';
/** chosen modifiers (missing / false = off) */
export type RunMods = Partial<Record<ModId, boolean>>;
export interface ModDef { id: ModId; name: string; /** shard bonus share (0.2 = +20 %) */ bonus: number; desc: string }

// ---- tuning per modifier ----
/** Swarm: field target, top-up rate, minute-wave size and swarm-ring size multipliers (the quality tier's maxEnemies still caps) */
export const SWARM_TARGET = 1.5, SWARM_REFILL = 1.6, SWARM_WAVE = 1.5, SWARM_RING = 1.25;
/** Swarm: minute waves / swarm rings may grow up to these sizes (were 40 / 48) */
export const SWARM_WAVE_MAX = 60, SWARM_RING_MAX = 60;
/** Tougher Foes: foe health and foe hit multipliers (bosses and guardians included) */
export const TOUGH_HP = 1.5, TOUGH_DMG = 1.35;
/** Hurry: real seconds per second of the run schedule — boss / guardian times, waves, elites and the difficulty ramp all
 *  arrive in 0.67 of the time (a 15:00 run's Tyrant at ~10:03, a 30:00 run's at ~20:06). The on-screen timer stays real. */
export const HURRY_K = 0.67;
/** Early Elites: first elite from this schedule time (normally 90 s), and the gap between elites × EARLY_ELITE_GAP */
export const EARLY_ELITE_FROM = 60, EARLY_ELITE_GAP = 0.6;
/** Relaxed (difficulty preset, owner 2026-11-02): foe health and foe hit multipliers (below 1; bosses and guardians included),
 *  XP motes are worth RELAXED_XP, and health drops are more frequent: elites drop a Salve with RELAXED_SALVE_ELITE (normally
 *  0.35), ordinary kills may drop one every RELAXED_SALVE_GAP s (normally 25) with RELAXED_SALVE_CHANCE x the usual chance,
 *  and field Mends are RELAXED_MEND x as likely. Shards: RELAXED_BONUS (a penalty, so it is not a farm). */
export const RELAXED_HP = 0.75, RELAXED_DMG = 0.75, RELAXED_XP = 1.25;
export const RELAXED_SALVE_ELITE = 0.6, RELAXED_SALVE_GAP = 15, RELAXED_SALVE_CHANCE = 2, RELAXED_MEND = 1.5;
export const RELAXED_BONUS = -0.3;
/** normal first-elite time (s) */
export const ELITE_FROM = 90;
/** No Healing: traits whose only effect is healing (Renewal, Siphon, Lifedraw). They leave the draft pool, except rank 1 while a held
 *  skill still needs one to evolve (Sim.noHealCatalyst). Stillness and Ember Heart also give Ki, so they stay. */
export const NOHEAL_TRAITS: readonly PassiveId[] = ['regen', 'siphon', 'lifedraw']; // Lifedraw (group A, owner 2026-11-03): healing only

export const MODS: readonly ModDef[] = [
  { id: 'hyper', name: 'Hyper', bonus: 0.2, desc: 'Everything moves fast: you, the foes and every shot travel at 110% of the original pace instead of the calmer 85%. Foe attacks wind up faster too.' },
  { id: 'swarm', name: 'Swarm', bonus: 0.25, desc: 'Bigger, denser hordes that build up faster from the very start: 1.5x the foes on the field, refilling 1.6x as fast, and 1.5x larger waves.' },
  { id: 'tough', name: 'Tougher Foes', bonus: 0.3, desc: 'Every foe, guardian and boss has 1.5x health and hits 1.35x as hard.' },
  { id: 'hurry', name: 'Hurry', bonus: 0.15, desc: 'The run\'s schedule is squeezed: guardians, the final boss, waves and the difficulty climb all arrive in two thirds of the time. The timer still shows real time.' },
  { id: 'noheal', name: 'No Healing', bonus: 0.2, desc: 'Nothing heals you: no health pickups drop, and regeneration, life-steal and healing rewards do nothing. Max-health gains still count.' },
  { id: 'elites', name: 'Early Elites', bonus: 0.2, desc: 'Elites start hunting you from 1:00 (normally 1:30) and arrive two-thirds more often.' },
  { id: 'night', name: 'Eternal Night', bonus: 0.15, desc: 'The arena stays at night whatever time of day you picked, with the heavier night horde.' },
  { id: 'relaxed', name: 'Relaxed', bonus: RELAXED_BONUS, desc: 'Foes have 25% less health and hit 25% softer, XP is worth 25% more, health drops come more often. Shards are cut by 30%: for learning, not farming.' },
];
export const MOD_IDS: readonly ModId[] = MODS.map((m) => m.id);
export const modDef = (id: ModId): ModDef => MODS.find((m) => m.id === id)!;

/** modifiers that cannot be on together: Relaxed softens exactly what Tougher Foes and No Healing harden */
export const MOD_CONFLICTS: Readonly<Record<ModId, readonly ModId[]>> = {
  hyper: [], swarm: [], hurry: [], elites: [], night: [],
  tough: ['relaxed'], noheal: ['relaxed'], relaxed: ['tough', 'noheal'],
};

/** the modifiers that are on, in display order */
export const modList = (m: RunMods | undefined): ModId[] => (m ? MOD_IDS.filter((id) => !!m[id]) : []);
/** total Fracture Shard bonus share (0.45 = +45 %); bonuses add up */
export function modShardBonus(m: RunMods | undefined): number {
  let s = 0; for (const id of modList(m)) s += modDef(id).bonus;
  return Math.round(s * 100) / 100;
}
/** shards paid with the bonus applied (rounded down) */
export const applyModBonus = (shards: number, m: RunMods | undefined): number => Math.floor(shards * (1 + modShardBonus(m)) + 1e-9);
/** the pace a run plays at */
export const paceFor = (m: RunMods | undefined): number => (m?.hyper ? PACE_HYPER : PACE);
/** device storage form: comma-separated ids (unknown ids are dropped) */
export const modsToString = (m: RunMods | undefined): string => modList(m).join(',');
export function modsFromString(s: string | null | undefined): RunMods {
  const out: RunMods = {};
  for (const id of (s ?? '').split(',')) if ((MOD_IDS as readonly string[]).includes(id)) out[id as ModId] = true;
  return out;
}
/** '+45%' / '−30%' style label */
export const bonusLabel = (share: number): string => `${share < 0 ? '−' : '+'}${Math.abs(Math.round(share * 100))}%`;

// ---- difficulty presets (owner 2026-11-02): one-tap bundles of the modifiers above ----
export type PresetId = 'relaxed' | 'normal' | 'hard' | 'brutal';
export type DifficultyId = PresetId | 'custom';
export interface DifficultyDef { id: DifficultyId; name: string; mods: readonly ModId[]; desc: string }
/** Hard = the three "more foes" modifiers (Swarm, Tougher Foes, Early Elites): +75 % shards, no change to the clock or healing.
 *  Brutal = Hard + the three that remove your margin (Hurry: everything arrives sooner, No Healing, Hyper: fast pace): +130 %. */
export const DIFFICULTIES: readonly DifficultyDef[] = [
  { id: 'relaxed', name: 'Relaxed', mods: ['relaxed'], desc: 'Learn the arena: foes have 25% less health and hit 25% softer, more XP and healing drops. Shards −30%.' },
  { id: 'normal', name: 'Normal', mods: [], desc: 'The intended run. No modifiers, no shard bonus.' },
  { id: 'hard', name: 'Hard', mods: ['swarm', 'tough', 'elites'], desc: 'Bigger hordes, tougher foes and elites from 1:00. Shards +75%.' },
  { id: 'brutal', name: 'Brutal', mods: ['swarm', 'tough', 'elites', 'hurry', 'noheal', 'hyper'], desc: 'Hard, plus a squeezed schedule, no healing at all and a fast pace. Shards +130%.' },
  { id: 'custom', name: 'Custom', mods: [], desc: 'Your own mix of modifiers. Picking a preset replaces it.' },
];
export const difficultyDef = (id: DifficultyId): DifficultyDef => DIFFICULTIES.find((d) => d.id === id)!;
export const isDifficulty = (s: unknown): s is DifficultyId => DIFFICULTIES.some((d) => d.id === s);
/** the modifier set a preset switches on ('custom' = none; selectDifficulty keeps the current set instead) */
export const presetMods = (id: DifficultyId): RunMods => Object.fromEntries(difficultyDef(id).mods.map((m) => [m, true]));
/** the preset whose modifiers are exactly `m`, else 'custom' (used for QA routes and old device settings) */
export function difficultyOf(m: RunMods | undefined): DifficultyId {
  const on = modList(m).join(',');
  for (const d of DIFFICULTIES) if (d.id !== 'custom' && MOD_IDS.filter((x) => d.mods.includes(x)).join(',') === on) return d.id;
  return 'custom';
}

/** Run Setup state: the difficulty row plus the modifier checkboxes. Pure, so the rules are unit-tested. */
export interface SetupMods { difficulty: DifficultyId; mods: RunMods }
/** pick a preset: it sets the checkboxes. Custom keeps the current ones (a starting point to edit). */
export function selectDifficulty(s: SetupMods, id: DifficultyId): SetupMods {
  return id === 'custom' ? { difficulty: 'custom', mods: { ...s.mods } } : { difficulty: id, mods: presetMods(id) };
}
/** step through the four presets with left / right (Custom is entered by editing a checkbox, or by picking it) */
export function stepDifficulty(s: SetupMods, d: number): SetupMods {
  const ids: PresetId[] = ['relaxed', 'normal', 'hard', 'brutal'];
  const i = ids.indexOf(s.difficulty as PresetId);
  const next = i < 0 ? (d > 0 ? 0 : ids.length - 1) : (i + d + ids.length) % ids.length;
  return selectDifficulty(s, ids[next]);
}
/** toggle one checkbox by hand: the row switches to Custom; modifiers that contradict the new one switch off */
export function toggleMod(s: SetupMods, id: ModId): SetupMods {
  const mods: RunMods = { ...s.mods };
  if (mods[id]) delete mods[id];
  else { mods[id] = true; for (const c of MOD_CONFLICTS[id]) delete mods[c]; }
  return { difficulty: 'custom', mods };
}
/** restore from device storage: a stored preset only counts while the stored modifiers still match it */
export function restoreSetup(diff: string | null | undefined, modsStr: string | null | undefined): SetupMods {
  const mods = modsFromString(modsStr);
  const d = isDifficulty(diff) ? diff : difficultyOf(mods);
  return d === 'custom' || difficultyOf(mods) === d ? { difficulty: d, mods } : { difficulty: difficultyOf(mods), mods };
}

// ---- CHALLENGES (2026-11-02): challenge-only rule flags on top of the modifiers (src/app/feats.ts CHALLENGES). They reach
// the Sim through SimOptions.rules, so they stay deterministic and unit-testable. Normal runs never set them.
export interface ChallengeRules {
  /** skill slots for the run (default MAX_WEAPONS = 6; Spirit Bolt takes one) */
  skillSlots?: number;
  /** extra random skills at rank 1 on top of Spirit Bolt (never more than the free slots) */
  startSkills?: number;
}
/** the skill-slot cap a run plays with (1..max) */
export const ruleSlots = (r: ChallengeRules | undefined, max: number): number =>
  Math.max(1, Math.min(max, Math.floor(r?.skillSlots ?? max)));
/** the extra starting skills for a seed: distinct, offerable (not retired, not innate, not Spirit Bolt), seeded own stream */
export function ruleStartSkills(r: ChallengeRules | undefined, seed: number, free: number): WeaponId[] {
  const n = Math.max(0, Math.min(free, Math.floor(r?.startSkills ?? 0)));
  if (!n) return [];
  const pool = (Object.keys(WEAPONS) as WeaponId[]).filter((id) => id !== 'bolt' && !WEAPONS[id].retired && !INNATE.includes(id));
  const rng = new Rng((seed * 2654435761 + 0x5eed) >>> 0), out: WeaponId[] = [];
  while (out.length < n && pool.length) out.push(pool.splice(rng.int(pool.length), 1)[0]);
  return out;
}
